import { useReinsurerAverageOfferAcceptanceRate } from "hooks/useRIFacPremium";
import {
    Clock,
    Target,
    TrendingDown,
    TrendingUp,
    Minus,
    Loader,
    AlertCircle,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PERIOD_VALUES } from "utils";

type PeriodType = keyof typeof PERIOD_VALUES;

const PerformanceRate: React.FC = () => {
    const { t } = useTranslation();
    const [period, setPeriod] = useState<PeriodType>("7d");
    const { averageOfferAcceptanceRate, loading, error } =
        useReinsurerAverageOfferAcceptanceRate(PERIOD_VALUES[period]);

    const processedData = useMemo(() => {
        if (!averageOfferAcceptanceRate) {
            return {
                averageTime: 0,
                fastTime: 0,
                slowestTime: 0,
                totalOffers: 0,
                timeChange: 0,
                isImproved: false,
                hasNoChange: false,
            };
        }

        const parseTimeToMinutes = (timeString: string): number => {
            if (!timeString) return 0;
            const parts = timeString.split(":");
            if (parts.length !== 3) return 0;

            const hours = parseInt(parts[0], 10) || 0;
            const minutes = parseInt(parts[1], 10) || 0;
            const seconds = parseInt(parts[2], 10) || 0;

            return hours * 60 + minutes + seconds / 60;
        };

        return {
            averageTime: parseTimeToMinutes(
                averageOfferAcceptanceRate.average_acceptance_time ?? "",
            ),
            fastTime: parseTimeToMinutes(
                averageOfferAcceptanceRate.fast_time ?? "",
            ),
            slowestTime: parseTimeToMinutes(
                averageOfferAcceptanceRate.slowest_time ?? "",
            ),
            totalOffers: averageOfferAcceptanceRate.total_offers || 0,
            timeChange: averageOfferAcceptanceRate?.time_change || 0,
            isImproved:
                averageOfferAcceptanceRate?.time_change &&
                averageOfferAcceptanceRate?.time_change > 0,
            hasNoChange: averageOfferAcceptanceRate?.time_change === 0,
        };
    }, [averageOfferAcceptanceRate]);

    const formatTimeString = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        const seconds = Math.floor((minutes - Math.floor(minutes)) * 60);

        if (hours > 0) {
            return `${hours}h ${mins}m`;
        } else if (mins > 0) {
            return `${mins}m${seconds > 0 ? ` ${seconds}s` : ""}`;
        }
        return `${seconds}s`;
    };

    const renderPeriodButton = (p: PeriodType) => (
        <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                period === p
                    ? "bg-green-600 text-white shadow-sm"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}>
            {p.toUpperCase()}
        </button>
    );

    const renderTrendIndicator = () => {
        if (processedData.hasNoChange) {
            return (
                <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-lg">
                    <Minus className="text-gray-500 mr-2" size={16} />
                    <span className="text-sm text-gray-700">
                        {t("dashboard.performance.no_change", { period })}
                    </span>
                </div>
            );
        }

        const changeText = processedData.isImproved
            ? t("dashboard.performance.faster", {
                  change: Math.abs(processedData.timeChange).toFixed(1),
                  period,
              })
            : t("dashboard.performance.slower", {
                  change: Math.abs(processedData.timeChange).toFixed(1),
                  period,
              });

        return (
            <div
                className={`flex items-center px-3 py-1.5 rounded-lg ${
                    processedData.isImproved ? "bg-green-50" : "bg-amber-50"
                }`}>
                {processedData.isImproved ? (
                    <TrendingDown className="text-green-600 mr-2" size={16} />
                ) : (
                    <TrendingUp className="text-amber-600 mr-2" size={16} />
                )}
                <span
                    className={`text-sm ${
                        processedData.isImproved
                            ? "text-green-700"
                            : "text-amber-700"
                    }`}>
                    {changeText}
                </span>
            </div>
        );
    };

    const isTargetMet = processedData.averageTime < 60;

    if (loading) {
        return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center text-gray-500">
                        <Loader className="animate-spin h-8 w-8 text-green-500 mb-3" />
                        <span className="text-sm font-medium">
                            {t("dashboard.performance.loading")}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center text-red-500">
                        <AlertCircle className="h-8 w-8 mb-3" />
                        <span className="text-sm font-medium">
                            {t("dashboard.performance.error")}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                        {t("dashboard.performance.title")}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {t("dashboard.performance.description")}
                    </p>
                </div>
                <div className="flex space-x-2">
                    {Object.keys(PERIOD_VALUES).map((p) =>
                        renderPeriodButton(p as PeriodType),
                    )}
                </div>
            </div>

            <div className="flex flex-col items-center justify-center mb-8">
                <div className="text-sm text-gray-500 mb-2">
                    {t("dashboard.performance.average_response_time")}
                </div>
                <div className="flex items-center justify-center mb-2">
                    <div className="relative">
                        <Clock className="text-green-500 mr-3" size={32} />
                        {isTargetMet && (
                            <div className="absolute -top-1 -right-1 bg-green-500 rounded-full w-5 h-5 flex items-center justify-center">
                                <svg
                                    className="w-3 h-3 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                        )}
                    </div>
                    <span className="text-4xl font-bold text-gray-900">
                        {formatTimeString(processedData.averageTime)}
                    </span>
                </div>
                {renderTrendIndicator()}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gray-50 p-4 rounded-xl">
                    <div className="text-xs text-gray-500 mb-1">
                        {t("dashboard.performance.fastest")}
                    </div>
                    <div className="text-xl font-semibold text-gray-900">
                        {formatTimeString(processedData.fastTime)}
                    </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                    <div className="text-xs text-gray-500 mb-1">
                        {t("dashboard.performance.slowest")}
                    </div>
                    <div className="text-xl font-semibold text-gray-900">
                        {formatTimeString(processedData.slowestTime)}
                    </div>
                </div>
                <div
                    className={`${isTargetMet ? "bg-green-50" : "bg-red-50"} p-4 rounded-xl`}>
                    <div className="text-xs text-gray-500 mb-1 flex items-center">
                        <Target className="mr-1" size={14} />
                        {t("dashboard.performance.target")}
                    </div>
                    <div
                        className={`text-xl font-semibold ${isTargetMet ? "text-green-600" : "text-red-600"}`}>
                        &lt; 1h
                    </div>
                    <div
                        className={`text-xs mt-1 ${isTargetMet ? "text-green-500" : "text-red-500"}`}>
                        {isTargetMet
                            ? t("dashboard.performance.target_met")
                            : t("dashboard.performance.target_not_met")}
                    </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                    <div className="text-xs text-gray-500 mb-1">
                        {t("dashboard.performance.total_offers")}
                    </div>
                    <div className="text-xl font-semibold text-gray-900">
                        {processedData.totalOffers.toLocaleString()}
                    </div>
                </div>
            </div>

            <div className="mt-auto pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-500 text-center">
                    {t("dashboard.performance.metrics_based_on", {
                        count: processedData.totalOffers,
                    })}
                </div>
            </div>
        </div>
    );
};

export default PerformanceRate;
